import { useState } from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
const CURRENT_MONTH = 2; // March (0-indexed) - 2025

// ── DATA ────────────────────────────────────────────────────────────────────

const revenueByYear: Record<number, (number | null)[]> = {
  2023: [1800, 2100, 2400, 1900, 2600, 3100, 2900, 3400, 3800, 4200, 4600, 5100],
  2024: [4800, 5100, 5600, 5200, 6100, 6800, 7200, 7900, 7600, 8400, 9100, 9500],
  2025: [9200, 9840, 10200, null, null, null, null, null, null, null, null, null],
};

const clientsByYear: Record<number, (number | null)[]> = {
  2023: [2, 3, 4, 4, 5,  6,  6,  7,  8,  9, 10, 11],
  2024: [11, 11, 12, 12, 13, 14, 14, 15, 15, 16, 16, 17],
  2025: [17, 17, 18, null, null, null, null, null, null, null, null, null],
};

const projectsByYear: Record<number, (number | null)[]> = {
  2023: [1, 2, 3, 2, 4, 5, 5, 6, 7, 7, 8, 9],
  2024: [9, 9, 10, 10, 11, 12, 12, 13, 13, 14, 14, 15],
  2025: [15, 16, 17, null, null, null, null, null, null, null, null, null],
};

const serviceBreakdownByYear: Record<number, { label: string; value: number; color: string }[]> = {
  2023: [
    { label: 'Tvorba webov',    value: 18400, color: '#f97316' },
    { label: 'Marketing',       value: 9200,  color: '#fb923c' },
    { label: 'Sociálne siete',  value: 4100,  color: '#fdba74' },
    { label: 'Správa systémov', value: 2400,  color: '#fed7aa' },
  ],
  2024: [
    { label: 'Tvorba webov',    value: 38600, color: '#f97316' },
    { label: 'Marketing',       value: 22100, color: '#fb923c' },
    { label: 'Sociálne siete',  value: 11400, color: '#fdba74' },
    { label: 'Správa systémov', value: 6500,  color: '#fed7aa' },
  ],
  2025: [
    { label: 'Tvorba webov',    value: 14500, color: '#f97316' },
    { label: 'Marketing',       value: 8200,  color: '#fb923c' },
    { label: 'Sociálne siete',  value: 4400,  color: '#fdba74' },
    { label: 'Správa systémov', value: 2140,  color: '#fed7aa' },
  ],
};

function yearTotal(year: number) {
  return (revenueByYear[year] ?? []).reduce<number>((acc, v) => acc + (v ?? 0), 0);
}

function yoyGrowth(year: number) {
  const prev = yearTotal(year - 1);
  if (!prev) return null;
  return Math.round(((yearTotal(year) - prev) / prev) * 100);
}

// ── LINE CHART (SVG) ─────────────────────────────────────────────────────────

interface LineChartProps {
  data: (number | null)[];
  color: string;
  fillColor: string;
}

function LineChart({ data, color, fillColor }: LineChartProps) {
  const W = 800, H = 140, PAD = { t: 12, r: 12, b: 24, l: 40 };
  const valid = data.filter((v): v is number => v !== null);
  if (valid.length < 2) return null;

  const maxV = Math.max(...valid);
  const minV = Math.min(...valid) * 0.88;
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;

  const pts = data.map((v, i) => {
    if (v === null) return null;
    const x = PAD.l + (i / 11) * innerW;
    const y = PAD.t + innerH - ((v - minV) / (maxV - minV)) * innerH;
    return { x, y, v, i };
  }).filter((p): p is { x: number; y: number; v: number; i: number } => p !== null);

  const linePts = pts.map(p => `${p.x},${p.y}`).join(' ');
  const areaPath = `M${pts[0].x},${PAD.t + innerH} ` +
    pts.map(p => `L${p.x},${p.y}`).join(' ') +
    ` L${pts[pts.length - 1].x},${PAD.t + innerH} Z`;

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(pct => {
    const y = PAD.t + innerH * (1 - pct);
    const val = Math.round(minV + (maxV - minV) * pct);
    return { y, val };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: 140 }}>
      <defs>
        <linearGradient id={`areaGrad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillColor} stopOpacity="0.35" />
          <stop offset="100%" stopColor={fillColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Grid */}
      {gridLines.map(({ y, val }) => (
        <g key={y}>
          <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} stroke="#3f3f46" strokeWidth="1" strokeDasharray="4 4" />
          <text x={PAD.l - 6} y={y + 4} textAnchor="end" fill="#52525b" fontSize="11">{val}</text>
        </g>
      ))}

      {/* Month labels */}
      {MONTHS.map((m, i) => {
        const x = PAD.l + (i / 11) * innerW;
        return <text key={m} x={x} y={H - 4} textAnchor="middle" fill="#52525b" fontSize="10">{m}</text>;
      })}

      {/* Area */}
      <path d={areaPath} fill={`url(#areaGrad-${color.replace('#', '')})`} />

      {/* Line */}
      <polyline points={linePts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

      {/* Dots */}
      {pts.map(p => (
        <g key={p.i}>
          <circle cx={p.x} cy={p.y} r={4.5} fill="#18181b" stroke={color} strokeWidth="2.5" />
        </g>
      ))}
    </svg>
  );
}

// ── BAR CHART ────────────────────────────────────────────────────────────────

function BarChart({ year }: { year: number }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const data = revenueByYear[year] ?? [];
  const valid = data.filter((v): v is number => v !== null);
  const maxV = Math.max(...valid, 1);

  return (
    <div className="flex items-end gap-1.5 h-36 w-full">
      {data.map((v, i) => {
        const hasData = v !== null;
        const heightPct = hasData ? Math.max(4, Math.round((v / maxV) * 100)) : 0;
        const isCurrent = year === 2025 && i === CURRENT_MONTH;
        const isFuture = year === 2025 && i > CURRENT_MONTH;
        const isHov = hovered === i;

        return (
          <div
            key={i}
            className="flex-1 flex flex-col items-center gap-1 cursor-pointer relative group"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
          >
            {/* Tooltip */}
            {isHov && hasData && (
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-zinc-800 border border-zinc-700 text-zinc-200 text-[10px] font-semibold px-2 py-1 rounded-lg whitespace-nowrap z-10">
                {v!.toLocaleString()} €
              </div>
            )}

            <div className="w-full flex items-end" style={{ height: '128px' }}>
              {hasData ? (
                <div
                  className="w-full rounded-t-md transition-all duration-300"
                  style={{
                    height: `${heightPct}%`,
                    background: isCurrent
                      ? 'linear-gradient(180deg, #f97316 0%, #ea580c 100%)'
                      : isHov
                      ? 'rgba(249,115,22,0.6)'
                      : 'rgba(249,115,22,0.25)',
                  }}
                />
              ) : (
                <div
                  className="w-full rounded-t-md border border-dashed border-zinc-800"
                  style={{ height: '8px' }}
                />
              )}
            </div>

            <span className={`text-[9px] font-medium ${isCurrent ? 'text-orange-400' : isFuture ? 'text-zinc-700' : 'text-zinc-600'}`}>
              {MONTHS[i]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── SERVICE BREAKDOWN ────────────────────────────────────────────────────────

function ServiceBreakdown({ year }: { year: number }) {
  const services = serviceBreakdownByYear[year] ?? [];
  const total = services.reduce((s, r) => s + r.value, 0);
  return (
    <div className="space-y-3.5">
      {services.map((s) => {
        const pct = Math.round((s.value / total) * 100);
        return (
          <div key={s.label}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                <span className="text-zinc-400 text-xs">{s.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-[10px]">{pct} %</span>
                <span className="text-white text-xs font-bold">{s.value.toLocaleString()} €</span>
              </div>
            </div>
            <div className="w-full bg-zinc-800 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: s.color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────

interface AdminChartsProps {
  year: number;
  onYearChange: (y: number) => void;
}

export default function AdminCharts({ year, onYearChange }: AdminChartsProps) {
  const growth = yoyGrowth(year);
  const totalRevenue = yearTotal(year);
  const prevRevenue = yearTotal(year - 1);

  const clientData = clientsByYear[year] ?? [];
  const validClients = clientData.filter((v): v is number => v !== null);
  const lastClients = validClients[validClients.length - 1] ?? 0;

  const projectData = projectsByYear[year] ?? [];
  const validProjects = projectData.filter((v): v is number => v !== null);
  const lastProjects = validProjects[validProjects.length - 1] ?? 0;

  return (
    <div className="space-y-4">

      {/* ── Year switcher header ── */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-white font-bold text-base">Prehľad výkonu</p>
            <p className="text-zinc-500 text-xs mt-0.5">
              {year === 2025 ? 'Akt. rok - Jan–Mar 2025' : `Celý rok ${year}`}
            </p>
          </div>

          {/* Year pills */}
          <div className="flex items-center bg-zinc-800/60 rounded-xl p-1 gap-1 self-start sm:self-auto">
            {[2023, 2024, 2025].map((y) => (
              <button
                key={y}
                onClick={() => onYearChange(y)}
                className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  year === y
                    ? 'bg-orange-500 text-white shadow-lg'
                    : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/60'
                }`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* Total revenue */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <p className="text-zinc-500 text-xs mb-1">Celkový obrat</p>
            <p className="text-white text-2xl font-black">{totalRevenue.toLocaleString()} €</p>
            {growth !== null && (
              <div className={`flex items-center gap-1 mt-1 ${growth >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                <i className={`ri-arrow-${growth >= 0 ? 'up' : 'down'}-line text-xs`} />
                <span className="text-xs font-semibold">{Math.abs(growth)} % vs {year - 1}</span>
              </div>
            )}
            {growth === null && (
              <p className="text-zinc-600 text-xs mt-1">Prvý sledovaný rok</p>
            )}
          </div>

          {/* Clients */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <p className="text-zinc-500 text-xs mb-1">Klienti (koniec roka)</p>
            <p className="text-white text-2xl font-black">{lastClients}</p>
            {year > 2023 && (
              <div className="flex items-center gap-1 mt-1 text-sky-400">
                <i className="ri-user-add-line text-xs" />
                <span className="text-xs font-semibold">
                  +{lastClients - (clientsByYear[year - 1]?.filter((v): v is number => v !== null).slice(-1)[0] ?? 0)} nových
                </span>
              </div>
            )}
          </div>

          {/* Projects */}
          <div className="bg-zinc-800/50 rounded-xl p-4">
            <p className="text-zinc-500 text-xs mb-1">Projekty (celkom)</p>
            <p className="text-white text-2xl font-black">{lastProjects}</p>
            {year > 2023 && (
              <div className="flex items-center gap-1 mt-1 text-amber-400">
                <i className="ri-folder-add-line text-xs" />
                <span className="text-xs font-semibold">
                  +{lastProjects - (projectsByYear[year - 1]?.filter((v): v is number => v !== null).slice(-1)[0] ?? 0)} projektov
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Revenue bar chart */}
        <div className="mb-1">
          <div className="flex items-center justify-between mb-3">
            <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Mesačný obrat (€)</p>
            {prevRevenue > 0 && (
              <p className="text-zinc-600 text-[10px]">
                Minulý rok celkom: {prevRevenue.toLocaleString()} €
              </p>
            )}
          </div>
          <BarChart year={year} />
        </div>
      </div>

      {/* ── Bottom row: line charts + service ── */}
      <div className="grid grid-cols-5 gap-4">

        {/* Client growth */}
        <div className="col-span-3 bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-white font-semibold text-sm">Rast klientov</p>
              <p className="text-zinc-500 text-xs mt-0.5">Kumulatívny počet aktívnych klientov</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-sky-400" />
                <span className="text-zinc-500 text-xs">Klienti</span>
              </div>
              <div className="flex items-center gap-1.5 ml-2">
                <div className="w-2 h-2 rounded-full bg-violet-400" />
                <span className="text-zinc-500 text-xs">Projekty</span>
              </div>
            </div>
          </div>

          <div className="relative mb-2">
            <LineChart data={clientsByYear[year] ?? []} color="#38bdf8" fillColor="#38bdf8" />
          </div>
          <div className="relative">
            <LineChart data={projectsByYear[year] ?? []} color="#a78bfa" fillColor="#a78bfa" />
          </div>
        </div>

        {/* Service breakdown */}
        <div className="col-span-2 bg-zinc-900 border border-zinc-800 rounded-2xl p-5">
          <div className="mb-5">
            <p className="text-white font-semibold text-sm">Obrat podľa služby</p>
            <p className="text-zinc-500 text-xs mt-0.5">{year} · celkom {totalRevenue.toLocaleString()} €</p>
          </div>

          {/* Mini donut visual (conic-gradient) */}
          <div className="flex justify-center mb-5">
            <div
              className="w-28 h-28 rounded-full"
              style={{
                background: (() => {
                  const services = serviceBreakdownByYear[year] ?? [];
                  const total = services.reduce((s, r) => s + r.value, 0);
                  let cumPct = 0;
                  const stops = services.map(s => {
                    const pct = (s.value / total) * 100;
                    const from = cumPct;
                    cumPct += pct;
                    return `${s.color} ${from.toFixed(1)}% ${cumPct.toFixed(1)}%`;
                  }).join(', ');
                  return `conic-gradient(${stops})`;
                })(),
              }}
            >
              <div className="w-full h-full rounded-full flex items-center justify-center" style={{ margin: '22%', width: '56%', height: '56%', background: '#18181b' }}>
                <span className="text-white text-xs font-bold">{year}</span>
              </div>
            </div>
          </div>

          <ServiceBreakdown year={year} />
        </div>
      </div>
    </div>
  );
}
